import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";

// import SourceFilters from "@/pages/chat/components/filter/SourceFilters";
import { Checkbox } from "@capacit-com/lara-law-shared-ui";
import { Label } from "@capacit-com/lara-law-shared-ui";
import { ScrollArea } from "@capacit-com/lara-law-shared-ui";
import { Scale } from "lucide-react";
import { cn } from "@/lib/utils";
import { useContent } from "@/hooks/useContent";
import { useSourceSelectionDialog } from "@/pages/chat/hooks/useSourceSelectionDialog";
import { stringToSearchOptionType } from "@/pages/chat/utils/PineconeQueries";
import { normalizeDataSource, DATASOURCE_CONFIG } from "@/config/datasources";
import useSourceFeatureFlags from "@/hooks/useSourceFeatureFlags";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContinue?: () => void;
};

const SourceSelectionDialog = ({ open, onOpenChange, onContinue }: Props) => {
  const { t } = useContent();
  const { isSourceEnabled } = useSourceFeatureFlags();

  const {
    availableSources,
    selectedSource,
    // filterValues,
    highlightedIndex,
    sourceListRef,
    hasWorkspaceContext,
    handleSourceRowClick,
    handleCheckboxChange,
    handleCreateFilters,
    handleSourceListKeyDown,
    // handleFilterChange,
    // handleFilterClear,
  } = useSourceSelectionDialog({ open, onOpenChange });

  const getDisabledReason = () => {
    if (hasWorkspaceContext) {
      return t("chat.sourceSelection.cannotAddSourceTooltip");
    }
    if (!selectedSource) {
      return t("chat.sourceSelection.selectSourcePrompt");
    }
    return "";
  };

  const isCreateDisabled = !selectedSource || hasWorkspaceContext;

  const filteredSources = availableSources?.filter((source) => {
    const result = stringToSearchOptionType(source.value);
    if (result === undefined) return false;

    const dataSource = normalizeDataSource(source.value);
    if (!dataSource || !DATASOURCE_CONFIG[dataSource]?.supportsChat)
      return false;

    return isSourceEnabled(source.value);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" className="flex flex-col">
        <DialogTitle className="text-xl font-semibold shrink-0 flex items-center gap-2 text-foreground">
          <Scale className="size-5 text-foreground" />
          {t("chat.sourceSelection.title")}
        </DialogTitle>

        {hasWorkspaceContext && (
          <div className="mt-2 p-3 bg-warning/10 border border-warning/20 rounded-md">
            <p className="text-sm text-warning-foreground">
              {t("chat.sourceSelection.cannotAddSourceWarning")}
            </p>
          </div>
        )}

        <div
          className={cn(
            "flex-1 overflow-hidden flex gap-4 min-h-0",
            hasWorkspaceContext && "pointer-events-none opacity-50"
          )}
        >
          {/* Source Selection Panel */}
          <div className="w-full flex flex-col min-h-0">
            <Label className="mb-3 text-sm font-medium shrink-0 text-foreground">
              {t("chat.sourceSelection.availableSources")}
            </Label>
            <ScrollArea className="flex-1 overflow-y-auto min-h-0">
              <div
                ref={sourceListRef}
                className="space-y-2 outline-none rounded-md transition-all pr-2"
                tabIndex={0}
                onKeyDown={handleSourceListKeyDown}
                role="listbox"
                aria-label={t("chat.sourceSelection.availableSources")}
              >
                {filteredSources?.map((source, index) => {
                  const isSelected = selectedSource?.value === source.value;
                  const isHighlighted = highlightedIndex === index;
                  return (
                    <div
                      key={source.value}
                      className={cn(
                        "flex items-center space-x-2 p-2 rounded-md transition-colors cursor-pointer",
                        isSelected
                          ? "bg-primary text-primary-foreground border border-primary"
                          : isHighlighted
                            ? "bg-accent text-accent-foreground border border-primary/30"
                            : "hover:bg-primary hover:text-primary-foreground border border-transparent"
                      )}
                      onClick={() => handleSourceRowClick(source)}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) =>
                          handleCheckboxChange(source, checked === true)
                        }
                        id={source.value}
                      />
                      <div className="flex-1 text-sm cursor-pointer">
                        {source.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* TODO: Re-enable when metadata filtering is restored */}
          {/* Filter Configuration Panel */}
          {/* <div className="flex-1 flex flex-col min-w-0">
            {selectedSource ? (
              <>
                <Label className="mb-3 text-sm font-medium text-foreground">
                  {t("chat.sourceSelection.configureFiltersFor")}{" "}
                  {selectedSource}
                </Label>
                <div
                  className="space-y-4 pr-4 outline-none rounded-md transition-all"
                  role="region"
                  aria-label={t("chat.sourceSelection.configureFiltersFor")}
                >
                  <SourceFilters
                    source={selectedSource}
                    filterValues={filterValues}
                    onFilterChange={handleFilterChange}
                    onFilterClear={handleFilterClear}
                  />
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-sm text-muted-foreground text-center">
                  {t("chat.sourceSelection.selectSourceToConfigure")}
                </p>
              </div>
            )}
          </div> */}
        </div>

        <DialogFooter className="shrink-0">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-muted-foreground">
              {selectedSource && (
                <span>{t("chat.sourceSelection.sourceSelected")}</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t("chat.sourceSelection.cancel")}
              </Button>
              <Button
                onClick={() => {
                  handleCreateFilters();
                  onContinue?.();
                }}
                disabled={isCreateDisabled}
                title={getDisabledReason()}
              >
                {t("chat.sourceSelection.addSource")}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SourceSelectionDialog;
